
import React, { useState, useEffect } from 'react';
import { calculatePouleRanking } from '../utils/rankingLogic';
import { Award, Download } from 'lucide-react';

const getAthleteName = (id, allAthletes, placeholder = 'À déterminer') => {
    if (!id) return placeholder;
    const a = allAthletes?.find(a => String(a.id) === String(id));
    if (a) return `${(a.nom || a.name || '').toUpperCase()} ${a.prenom || a.firstname || ''}`.trim();
    return placeholder;
};

const DecisionBadge = ({ match }) => {
    if (!match.winner_id || !match.decision_type) return null;
    return (
        <div style={{ fontSize: '10px', color: '#3182ce', marginTop: '2px', fontStyle: 'italic' }}>
            {match.decision_type.replace(/_/g, ' ')}
            {(match.warnings_a1 > 0 || match.warnings_a2 > 0) && ` • AVT: ${match.warnings_a1}/${match.warnings_a2}`}
        </div>
    );
};

// --- Matrice de poule ---
function PouleMatrix({ matches, athletes, allAthletes }) {
    const getVictories = (id) => matches.filter(m => String(m.winner_id) === String(id)).length;
    return (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '320px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={s.th}>Athlète</th>
                        {athletes.map(a => (
                            <th key={a.id} style={{ ...s.th, textAlign: 'center', maxWidth: '80px' }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80px' }} title={getAthleteName(a.id, allAthletes)}>
                                    {getAthleteName(a.id, allAthletes).split(' ')[0]}
                                </div>
                            </th>
                        ))}
                        <th style={{ ...s.th, textAlign: 'center', backgroundColor: '#f0fff4', color: '#276749' }}>V</th>
                    </tr>
                </thead>
                <tbody>
                    {athletes.map(aRow => (
                        <tr key={aRow.id}>
                            <td style={{ ...s.td, fontWeight: 'bold', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getAthleteName(aRow.id, allAthletes)}
                            </td>
                            {athletes.map(aCol => {
                                if (aRow.id === aCol.id) return <td key={aCol.id} style={{ ...s.td, backgroundColor: '#edf2f7' }} />;
                                const m = matches.find(match =>
                                    (String(match.athlete1_id) === String(aRow.id) && String(match.athlete2_id) === String(aCol.id)) ||
                                    (String(match.athlete1_id) === String(aCol.id) && String(match.athlete2_id) === String(aRow.id))
                                );
                                const isW = m?.winner_id && String(m.winner_id) === String(aRow.id);
                                const isL = m?.winner_id && String(m.winner_id) === String(aCol.id);
                                return (
                                    <td key={aCol.id} style={{ ...s.td, textAlign: 'center', fontWeight: 'bold', color: isW ? '#38a169' : isL ? '#e53e3e' : '#cbd5e0' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span>{isW ? 'V' : isL ? 'D' : '–'}</span>
                                            {m && <DecisionBadge match={m} />}
                                        </div>
                                    </td>
                                );
                            })}
                            <td style={{ ...s.td, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f0fff4', color: '#276749' }}>{getVictories(aRow.id)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// --- Arbre d'élimination ---
function BracketNode({ match, allAthletes }) {
    const w1 = String(match.winner_id) === String(match.athlete1_id);
    const w2 = String(match.winner_id) === String(match.athlete2_id);
    return (
        <div style={s.bracketNode}>
            <div style={{ ...s.nodeLine, fontWeight: w1 ? 'bold' : 'normal', color: w1 ? '#2d3748' : '#a0aec0' }}>
                {getAthleteName(match.athlete1_id, allAthletes, match.placeholder1)} {w1 && '🏆'}
            </div>
            <div style={{ ...s.nodeLine, borderTop: '1px solid #edf2f7', fontWeight: w2 ? 'bold' : 'normal', color: w2 ? '#2d3748' : '#a0aec0' }}>
                {getAthleteName(match.athlete2_id, allAthletes, match.placeholder2)} {w2 && '🏆'}
            </div>
            {match.decision_type && (
                <div style={{ padding: '4px 10px', backgroundColor: '#f8fafc', borderTop: '1px solid #edf2f7' }}>
                    <DecisionBadge match={match} />
                </div>
            )}
        </div>
    );
}

function BracketTree({ matches, allAthletes }) {
    const demis = matches.filter(m => /demi/i.test(m.poule_label));
    const finale = matches.filter(m => /finale/i.test(m.poule_label) && !/petite/i.test(m.poule_label));
    const petite = matches.filter(m => /petite/i.test(m.poule_label));

    return (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'flex', gap: '30px', padding: '16px', backgroundColor: '#f1f5f9', borderRadius: '8px', alignItems: 'center', justifyContent: 'center', minWidth: 'fit-content' }}>
                {demis.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                        <div style={s.stageTitle}>Demi-finales</div>
                        {demis.map(m => <BracketNode key={m.id} match={m} allAthletes={allAthletes} />)}
                    </div>
                )}
                {finale.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                        <div style={s.stageTitle}>Finale</div>
                        {finale.map(m => <BracketNode key={m.id} match={m} allAthletes={allAthletes} />)}
                        {petite.length > 0 && (
                            <div style={{ marginTop: '8px', width: '100%' }}>
                                <div style={s.stageTitle}>Petite Finale</div>
                                {petite.map(m => <BracketNode key={m.id} match={m} allAthletes={allAthletes} />)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// --- Bloc de résultats pour une catégorie ---
function CategoryResults({ cat, order, allAthletes }) {
    const catMatches = order.filter(m => String(m.category_id) === String(cat.id));
    if (catMatches.length === 0) return <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', fontStyle: 'italic' }}>Aucun résultat pour cette catégorie</div>;

    const byPoule = catMatches.reduce((acc, m) => {
        const label = m.poule_label || 'Poule Unique';
        if (!acc[label]) acc[label] = [];
        acc[label].push(m);
        return acc;
    }, {});

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.entries(byPoule).map(([pouleLabel, matches]) => {
                const isElim = /demi|finale|quart/i.test(pouleLabel);
                const pouleAthletes = allAthletes.filter(a =>
                    matches.some(m => String(m.athlete1_id) === String(a.id) || String(m.athlete2_id) === String(a.id))
                );
                const ranking = !isElim && pouleAthletes.length >= 2
                    ? calculatePouleRanking(matches, pouleAthletes)
                    : [];

                return (
                    <div key={pouleLabel} style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ backgroundColor: '#ebf8ff', padding: '10px 14px', borderBottom: '1px solid #bee3f8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Award size={14} color="#3182ce" />
                            <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#2b6cb0', textTransform: 'uppercase' }}>{pouleLabel}</span>
                        </div>

                        {/* Classement poule */}
                        {ranking.length > 0 && (
                            <div style={{ padding: '10px 14px', backgroundColor: '#f0f9ff', borderBottom: '1px solid #bae6fd' }}>
                                {ranking.map((res, idx) => (
                                    <div key={res.athlete.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '4px 0', borderBottom: idx < ranking.length - 1 ? '1px solid rgba(186,230,253,0.4)' : 'none' }}>
                                        <span style={{ width: '22px', textAlign: 'center' }}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}</span>
                                        <span style={{ flex: 1, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getAthleteName(res.athlete.id, allAthletes)}</span>
                                        <span style={{ fontWeight: 'bold', color: '#3182ce', flexShrink: 0 }}>{res.victories}V · {res.points}pts</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Tableau ou bracket */}
                        <div style={{ padding: '12px 14px' }}>
                            {isElim
                                ? <BracketTree matches={matches} allAthletes={allAthletes} />
                                : <PouleMatrix matches={matches} athletes={pouleAthletes} allAthletes={allAthletes} />
                            }
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// --- Export CSV ---
function buildCsv(categories, order, allAthletes) {
    let csv = '﻿Ring;Catégorie;Poule;Type;Assaut;Athlète 1;Athlète 2;Vainqueur;Décision;AVT A1;AVT A2\n';
    categories.forEach(cat => {
        order.filter(m => String(m.category_id) === String(cat.id)).forEach(m => {
            const a1 = allAthletes.find(a => String(a.id) === String(m.athlete1_id));
            const a2 = allAthletes.find(a => String(a.id) === String(m.athlete2_id));
            const winner = allAthletes.find(a => String(a.id) === String(m.winner_id));
            const n1 = a1 ? `${(a1.nom || '').toUpperCase()} ${a1.prenom || ''}`.trim() : (m.placeholder1 || '');
            const n2 = a2 ? `${(a2.nom || '').toUpperCase()} ${a2.prenom || ''}`.trim() : (m.placeholder2 || '');
            const nw = winner ? `${(winner.nom || '').toUpperCase()} ${winner.prenom || ''}`.trim() : (m.winner_id ? 'Bye' : '');
            csv += [
                `Ring ${m.ring_id || '?'}`, cat.name || '', m.poule_label || '',
                m.type === 'poule' ? 'Poule' : 'Élimination',
                m.match_ref, n1, n2, nw, m.decision_type || '', m.warnings_a1 || 0, m.warnings_a2 || 0
            ].join(';') + '\n';
        });
    });
    return csv;
}


// =========================================================
// COMPOSANT PRINCIPAL
// =========================================================
export default function ResultsView({ order, allAthletes, categories, isMobile = false }) {
    const catsWithMatches = categories.filter(cat => order.some(m => String(m.category_id) === String(cat.id)));

    const [selectedCatId, setSelectedCatId] = useState(catsWithMatches[0]?.id || null);

    useEffect(() => {
        if (catsWithMatches.length > 0 && !catsWithMatches.find(c => String(c.id) === String(selectedCatId))) {
            setSelectedCatId(catsWithMatches[0].id);
        }
    }, [catsWithMatches.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExport = () => {
        const csv = buildCsv(categories, order, allAthletes);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `resultats_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const selectedCat = catsWithMatches.find(c => String(c.id) === String(selectedCatId));

    if (catsWithMatches.length === 0) {
        return <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0' }}>Aucun résultat disponible pour le moment.</div>;
    }

    return (
        <div>
            {/* Barre de contrôles */}
            <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', marginBottom: '16px', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
                <select
                    value={selectedCatId || ''}
                    onChange={e => setSelectedCatId(e.target.value)}
                    style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '15px', fontWeight: '600', backgroundColor: 'white', cursor: 'pointer', flex: isMobile ? 'none' : '0 0 auto', maxWidth: isMobile ? '100%' : '400px', width: isMobile ? '100%' : 'auto' }}
                >
                    {catsWithMatches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <button onClick={handleExport}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#3182ce', color: 'white', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 }}>
                    <Download size={14} /> {isMobile ? 'Export CSV' : 'Exporter en CSV'}
                </button>
            </div>

            {/* Résultats de la catégorie sélectionnée */}
            {selectedCat && (
                <CategoryResults cat={selectedCat} order={order} allAthletes={allAthletes} />
            )}
        </div>
    );
}

const s = {
    th: { border: '1px solid #e2e8f0', padding: '7px 8px', textAlign: 'left', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap' },
    td: { border: '1px solid #e2e8f0', padding: '7px 8px', fontSize: '12px' },
    bracketNode: { width: '200px', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' },
    nodeLine: { padding: '7px 10px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', justifyContent: 'space-between' },
    stageTitle: { fontSize: '10px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', textAlign: 'center', marginBottom: '8px', letterSpacing: '0.05em' }
};
